import utils from "@/utils";
import { Controller } from "@hotwired/stimulus";
import { ContainmentStategy, OutsideTapDetector, OutsideTapListener } from "@/motion";
import { ValueDefinitionMap } from "@hotwired/stimulus/dist/types/core/value_properties";

/** 
 * A controller to open or close a custom dropdown menu, based on the data-dropdown-menu controller attribute.
 * 
 * Note: Moves the menu to the front of the document body (or dialog) in case the container has overflow:hidden 
 * See https://github.com/popperjs/popper-core/issues/112 for details
 **/
export default class DropdownMenuController extends Controller<HTMLElement> implements OutsideTapListener {
    static values = {modal: {type: Boolean, default: true}};
    static classes = ['menu'];
    static targets = ['toggle'];

    declare modalValue: boolean;
    declare readonly hasModalValue: boolean;
    declare readonly menuClass: string;
    declare readonly toggleTarget: HTMLElement;

    public menuElement: HTMLDialogElement; // Can't use target api, because the menu does not stay inside the dropdown element
    public outsideTapDetector: OutsideTapDetector;

    onOutsideTap(e: Event): void {
        this.close();
    }

    connect(): void {
        this.menuElement = this.element.querySelector<HTMLDialogElement>(`.${this.menuClass}`);
        this.menuElement.inert = true; // For some reason, this is needed to prevent document reflow when opening the dialog (was happening with the "popup details" dialog)
        this.menuElement.show(); // Needed in order to show the menu on top of a dialog
        this.menuElement.inert = false;
        this.expandToWidthOfToggle();
        // this.preventClipping();
        this.enableKeyboardNavigation();
        this.outsideTapDetector = new OutsideTapDetector(this, new ContainmentStategy(this.toggleTarget, this.menuElement));
        if(this.modalValue){
            this.outsideTapDetector.enable();
        }
        utils.onControllerDisconnect(this, utils.createPopper(this.toggleTarget, this.menuElement, { placement: 'bottom-start' }));
        this.element.dispatchEvent(new Event('menu-open', { bubbles: true }));
    }

    /** 
     * Can be wider if needed, based on the size of the text input or menu content. 
     * Can't use the dropdown element to determine the width because it might have added width due to left/right arrows or flex styling.
     **/
    private expandToWidthOfToggle() {
        this.menuElement.style.minWidth = this.toggleTarget.offsetWidth + 'px'; 
    }

    private enableKeyboardNavigation() { 
        utils.addControllerActionWithCleanup(this, {event: 'keydown.esc@window', method: 'close'});
        utils.addControllerActionWithCleanup(this, {event: 'keydown.tab@window', method: 'disableTab'});
        utils.addControllerActionWithCleanup(this, {event: 'keydown.down@window', method: 'focusNextItem'});
        utils.addControllerActionWithCleanup(this, {event: 'keydown.up@window', method: 'focusPreviousItem'});
        utils.addControllerActionWithCleanup(this, {event: 'keydown.enter@window', method: 'selectItem'});
    }

    close(): void { // Sends a common to the dialog element, to close it
        this.element.dispatchEvent(new Event('close'));
    }

    disableTab(e: Event) { // TODO: maybe use a dialog modal instead, to allow tabbing inside the menu
        e.preventDefault();
    }

    focusNextItem(e: Event) {
        e.preventDefault();
        let nextItem = this.getNextItem(document.activeElement as HTMLElement);
        if (nextItem) {
            nextItem.focus();
        }
    }

    private getNextItem(item: HTMLElement) {
        let items = this.getFocusableItems();
        let index = item ? items.indexOf(item) : -1;
        return index >= 0 ? items[index + 1] : items[0];
    }

    focusPreviousItem(e: Event) {
        e.preventDefault();
        let previousItem = this.getPreviousItem(document.activeElement as HTMLElement);
        if (previousItem) {
            previousItem.focus();
        } else {
            this.element.dispatchEvent(new Event('arrow-up-start'));
        }
    }

    private getPreviousItem(item: HTMLElement) {
        let items = this.getFocusableItems();
        let index = item ? items.indexOf(item) : -1;
        return index > 0 ? items[index - 1] : null;
    }

    private getFocusableItems() {
        return Array.from(this.menuElement.querySelectorAll<HTMLElement>('[tabindex], input[type="search"]'));
    }

    selectItem(e: Event) {
        let item = document.activeElement as HTMLElement;
        if (item && this.menuElement.contains(item)) {
            e.preventDefault();
            item.click();
        }
    }

    disconnect(): void {
        this.menuElement.close();
        if(this.modalValue){
            this.outsideTapDetector.disable();
        }
        this.element.dispatchEvent(new Event('menu-close', {bubbles: true}));
    }
}
